import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { fetchShareholderMeetings } from '../shareholderMeetingsApi';
import { getActiveMeetingRoom } from '../meetingRoomApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';
import './UsersPage.css';

export default function ShareholderMeetingsPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const [joiningId, setJoiningId] = useState(null);

  async function handleJoin(row) {
    setJoiningId(row.id);
    try {
      const room = await getActiveMeetingRoom(row.id);
      navigate(`/meeting-room/${room.roomToken}`);
    } catch {
      if (row.zoomLink) window.open(row.zoomLink, '_blank', 'noreferrer');
    } finally {
      setJoiningId(null);
    }
  }

  const dateLocale = language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
    : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';

  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        setRows(await fetchShareholderMeetings());
      } catch (e) {
        setError(e instanceof Error ? e.message : t('meetings.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => rows.filter((x) => {
    const q = query.trim();
    return !q || x.title.includes(q) || x.companyName.includes(q);
  }), [rows, query]);

  return (
    <div className="page users-page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="calendar" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('meetings.myTitle')}</span>
            <span className="list-page-card__sub">{t('meetings.shSub')}</span>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}

        {!loading && (
        <>
          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" placeholder={t('meetings.mySearch')} value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('meetings.colMeeting')}</th>
                  <th>{t('meetings.colCompany')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('meetings.colDuration')}</th>
                  <th>{t('meetings.colJoin')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, idx) => (
                  <tr key={row.id}>
                    <td className="td-num">{idx + 1}</td>
                    <td>
                      <strong>{row.title}</strong>
                      {row.agenda ? <div className="users-email">{row.agenda}</div> : null}
                    </td>
                    <td>{row.companyName}</td>
                    <td>{row.scheduledAtUtc ? new Date(row.scheduledAtUtc).toLocaleString(dateLocale) : ''}</td>
                    <td>{row.durationMinutes ? `${row.durationMinutes} ${t('meetings.minutes')}` : '—'}</td>
                    <td>
                      <button
                        className="btn btn--primary"
                        style={{ background: '#02c3a6', color: '#fff', border: 'none', opacity: joiningId === row.id ? 0.7 : 1 }}
                        disabled={joiningId === row.id}
                        onClick={() => handleJoin(row)}
                      >
                        🔗 {joiningId === row.id ? '…' : t('meetings.colJoin')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredRows.length === 0 && <div className="users-empty">{t('meetings.myNoRows')}</div>}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
